import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  applyDocumentAccent,
  getAccentFromSearchParams,
  getBootstrapAccent,
  isAccentId,
  persistAccentPreference,
} from "./applyAccent";
import {
  applyDocumentTheme,
  getBootstrapThemeMode,
  getThemeFromSearchParams,
  isThemeMode,
  persistThemePreference,
} from "./applyTheme";
import { DEFAULT_ACCENT, DEFAULT_THEME_MODE } from "./constants";
import { AccentId, ResolvedTheme, ThemeMode } from "./types";

type ThemeContextValue = {
  themeMode: ThemeMode;
  resolvedTheme: ResolvedTheme;
  accentId: AccentId;
  /** When true, theme/accent come from web-app redirect and cannot be changed here. */
  isThemeLocked: boolean;
  setThemeMode: (mode: ThemeMode) => void;
  setAccentId: (accent: AccentId) => void;
};

const ThemeContext = createContext<ThemeContextValue>({
  themeMode: DEFAULT_THEME_MODE,
  resolvedTheme: "light",
  accentId: DEFAULT_ACCENT,
  isThemeLocked: false,
  setThemeMode: () => undefined,
  setAccentId: () => undefined,
});

export const useTheme = () => useContext(ThemeContext);

type ThemeProviderProps = {
  children: React.ReactNode;
  /**
   * When true (ads AccountConnection flow), prefer URL `theme`/`accent` and
   * lock selectors so connector matches the web-app appearance.
   */
  lockToUrlTheme?: boolean;
  /** Optional explicit initial mode (overrides URL/storage bootstrap). */
  initialThemeMode?: ThemeMode | null;
  /** Optional explicit initial accent (overrides URL/storage bootstrap). */
  initialAccentId?: AccentId | null;
};

const ThemeProvider = ({
  children,
  lockToUrlTheme = false,
  initialThemeMode = null,
  initialAccentId = null,
}: ThemeProviderProps) => {
  const urlTheme = getThemeFromSearchParams();
  const urlAccent = getAccentFromSearchParams();
  const locked = Boolean(lockToUrlTheme);

  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
    if (isThemeMode(initialThemeMode)) {
      return initialThemeMode;
    }
    if (lockToUrlTheme && urlTheme) {
      return urlTheme;
    }
    return getBootstrapThemeMode();
  });
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => {
    const mode = isThemeMode(initialThemeMode)
      ? initialThemeMode
      : lockToUrlTheme && urlTheme
        ? urlTheme
        : getBootstrapThemeMode();
    return applyDocumentTheme(mode);
  });
  const [accentId, setAccentIdState] = useState<AccentId>(() => {
    if (isAccentId(initialAccentId)) {
      return applyDocumentAccent(initialAccentId);
    }
    if (lockToUrlTheme && urlAccent) {
      return applyDocumentAccent(urlAccent);
    }
    return applyDocumentAccent(getBootstrapAccent());
  });

  const setThemeMode = useCallback(
    (mode: ThemeMode) => {
      if (locked) {
        return;
      }
      persistThemePreference(mode);
      setThemeModeState(mode);
      setResolvedTheme(applyDocumentTheme(mode));
    },
    [locked],
  );

  const setAccentId = useCallback(
    (accent: AccentId) => {
      if (locked) {
        return;
      }
      persistAccentPreference(accent);
      setAccentIdState(applyDocumentAccent(accent));
    },
    [locked],
  );

  // Keep in sync if parent passes a new locked URL theme
  useEffect(() => {
    if (!isThemeMode(initialThemeMode)) {
      return;
    }
    setThemeModeState(initialThemeMode);
    setResolvedTheme(applyDocumentTheme(initialThemeMode));
    // Persist so OAuth round-trips (which drop query params) keep the same mode.
    persistThemePreference(initialThemeMode);
  }, [initialThemeMode]);

  useEffect(() => {
    if (!isAccentId(initialAccentId)) {
      return;
    }
    setAccentIdState(applyDocumentAccent(initialAccentId));
    persistAccentPreference(initialAccentId);
  }, [initialAccentId]);

  // React to OS preference when mode is system
  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      setThemeModeState((current) => {
        if (current === "system") {
          setResolvedTheme(applyDocumentTheme("system"));
        }
        return current;
      });
    };

    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", onChange);
      return () => media.removeEventListener("change", onChange);
    }

    media.addListener(onChange);
    return () => media.removeListener(onChange);
  }, []);

  const value = useMemo(
    () => ({
      themeMode,
      resolvedTheme,
      accentId,
      isThemeLocked: locked,
      setThemeMode,
      setAccentId,
    }),
    [themeMode, resolvedTheme, accentId, locked, setThemeMode, setAccentId],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export default ThemeProvider;
